<template>
  <slot v-if="!url"></slot>

  <modal :id="modalId" :title="data.title" @shown="loadMatchedUsers">
    <div v-if="!linkedOrCreated" class="row text-center">
      <div class="col-6">
        <div class="text-center" v-if="userCreating">
          <i class="fa fa-spinner fa-spin fa-4x"></i>
        </div>
        <div v-else>
          <div v-if="error" class="alert alert-danger">{{ error }}</div>
          <p v-else>
            You are about to create CIMS user for <strong>{{ fullName }}</strong>
          </p>
        </div>

        <button type="button" class="btn btn-primary" v-if="!linkedOrCreated" :disabled="processing" @click="createUser">Create CIMS User</button>
      </div>

      <div class="col-6">
        You may also link the <strong>{{ fullName }}</strong> with an existing CIMS user

        <div class="text-center" v-if="matchedUserLoading">
          <i class="fa fa-spinner fa-spin fa-4x"></i>
        </div>
        <div v-else-if="!hasMatchedUsers" class="m-t-sm">
          <div class="alert alert-warning">
            No similar user found to link with.
          </div>
        </div>
        <div v-else class="m-t-sm">

          <div v-if="!matchedUserLinked">
            <div v-if="!matchedUserLinking">
            <select v-model="matchedUser" class="form-control">
              <option v-for="match in matchedUsers" :value="match">
                {{match.first_name}} {{match.last_name}}
              </option>
            </select>

            <div class="panel">
              <div class="panel-body">
                <dl class="row">
                  <dt class="col-md-6 text-right">User Info:</dt>
                  <dd class="col-md-6 text-left"><a :href="'/users/' + matchedUser.id">{{matchedUser.first_name}} {{matchedUser.last_name}}</a></dd>
                  <dt class="col-md-6 text-right">Matched Data:</dt>
                  <dd class="col-md-6 text-left">{{ matchedUser.matches.join(', ') }}</dd>
                </dl>
              </div>
            </div>

            <div v-if="linkError" class="alert alert-danger">{{ linkError }}</div>

            <button type="button" class="btn btn-primary" v-if="!linkedOrCreated" :disabled="processing" @click="linkUser">Link CIMS User</button>
            </div>
            <div v-else>
              <i class="fa fa-spinner fa-spin fa-4x"></i>
            </div>
          </div>

        </div>
      </div>
    </div>

    <div v-else class="row text-center">
      <div class="col-md-12">

        <div v-if="userCreated" class="alert alert-success">
          <p>{{ message }}</p>
          You can now <a :href="userLinks.view">view</a> or <a :href="userLinks.edit">edit</a> new user.
        </div>

        <div v-if="matchedUserLinked" class="alert alert-success">
          Linked with {{matchedUser.first_name}} {{matchedUser.last_name}}.
          You can <a :href="userLinks.view">view</a> or <a :href="userLinks.edit">edit</a> the user.
        </div>

      </div>
    </div>

    <template #footer>
        <button type="button" class="btn btn-default" v-if="linkedOrCreated" @click="hideModal">Close</button>
        <button type="button" class="btn btn-default" v-if="!linkedOrCreated" :disabled="processing" @click="hideModal">Cancel</button>
    </template>
  </modal>

  <slot name="hidden"/>
</template>

<script>
import HasModal from "../mixins/HasModal";
import { mapState, mapActions } from 'pinia'
import { useAdjusterToUserDataStore } from '../../stores/adjusterToUserData'

export default {
  mixins: [HasModal],

    props: {
      // Custom id for modal element.
      modalId: {
        type: String,
        default: 'adjuster-user-link-modal'
      },
      // Title for dialog.
      title: {
        type: String,
        default: ''
      },
      // Adjuster's first name.
      firstName: {
        type: String,
        default: ''
      },
      // Adjuster's last name.
      lastName: {
        type: String,
        default: ''
      },
      // Match user loader URL.
      matchUserUrl: {
        type: String,
        default: ''
      },
      // Link user URL.
      linkUserUrl: {
        type: String,
        default: ''
      },
      // Create user URL.
      createUserUrl: {
        type: String,
        required: true
      },
    },

    data() {
      return {
        data: {
          title: this.title,
          first_name: this.firstName,
          last_name: this.lastName,
        },
        userCreating: false,
        userCreated: false,
        message: '',
        error: '',
        userLinks: {
          view: '',
          edit: ''
        },
        matchedUserLoading: false,
        matchedUsers: [],
        matchedUser: null,
        hasMatchedUsers: false,
        matchedUserLinking: false,
        matchedUserLinked: false,
        linkError: '',
      };
    },

    computed: {
      ...mapState(useAdjusterToUserDataStore, ['url']),

      fullName() {
        return this.data.first_name + ' ' + this.data.last_name;
      },

      linkedOrCreated() {
        return this.userCreated || this.matchedUserLinked;
      },

      processing() {
        return this.userCreating || this.matchedUserLinking;
      }
    },

    methods: {
      ...mapActions(useAdjusterToUserDataStore, ['setData']),

      createUser() {
        this.userCreating = true;
        axios.post(this.createUserUrl)
          .then(response =>{
            this.userCreated = true;
            this.message = response.data.message;
            this.userLinks.view = response.data.url;
            this.userLinks.edit = response.data.urlEdit;

            this.setData({
              'url': response.data.url,
              'createdAt': response.data.createdAt,
              'firstName': response.data.firstName,
              'lastName': response.data.lastName,
              'status': response.data.status,
            });
          })
          .catch(error =>{
            if (error.response && error.response.status === 422) {
              this.error = 'Unable to create new user: ' + error.response.data.error + '.';
            } else {
              this.error = 'Unable to create new user.';
            }
          })
          .then(() =>{
            this.userCreating = false;
          });
      },

      linkUser() {
        this.matchedUserLinking = true;
        axios.post(this.linkUserUrl, {'idUser': this.matchedUser.id})
            .then(response =>{
              this.matchedUserLinked = true;
              this.userLinks.view = response.data.url;
              this.userLinks.edit = response.data.urlEdit;

              this.setData({
                'url': response.data.url,
                'createdAt': response.data.createdAt,
                'firstName': response.data.firstName,
                'lastName': response.data.lastName,
                'status': response.data.status,
              });
            })
            .catch(error =>{
              if (error.response && error.response.status === 422) {
                this.linkError = 'Unable to link the user: ' + error.response.data.error + '.';
              } else {
                this.linkError = 'Unable to link the user.';
              }
            })
            .then(() =>{
              this.matchedUserLinking = false;
            });
      },

      loadMatchedUsers() {
        this.matchedUserLoading = true;
        axios.get(this.matchUserUrl)
            .then(response =>{
              this.matchedUserLoading = false;
              if (response.data.matches) {
                this.matchedUsers = response.data.matches;
                if (response.data.matches.length) {
                  this.matchedUser = this.matchedUsers[0];
                  this.hasMatchedUsers = true;
                }
              }
            });
      },

    }
  }
</script>
